import { useState, useRef, useEffect } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { closeContentEnrichment } from '@/features/ui/uiSlice';
import styles from './ContentEnrichmentModal.module.css';

// ── System default categories always applied to every video ─────────────────
const SYSTEM_CATEGORIES = [
  {
    id: 'objects',
    label: 'Objects',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
    tags: ['Person', 'Car', 'Building', 'Table', 'Chair', 'Screen', 'Door', 'Window', 'Phone', 'Laptop', 'Bag', 'Bottle'],
  },
  {
    id: 'activities',
    label: 'Activities',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    tags: ['Walking', 'Talking', 'Presenting', 'Writing', 'Reading', 'Laughing', 'Running', 'Sitting', 'Standing', 'Shaking hands'],
  },
  {
    id: 'scenes',
    label: 'Scenes',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      </svg>
    ),
    tags: ['Office', 'Outdoor', 'Studio', 'Conference room', 'Event hall', 'Street', 'Cafe', 'Auditorium', 'Home', 'Warehouse'],
  },
  {
    id: 'people',
    label: 'People',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    tags: ['Speaker', 'Interviewer', 'Host', 'Audience', 'Customer', 'Employee', 'Executive', 'Expert'],
  },
  {
    id: 'topics',
    label: 'Topics',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    tags: ['Product demo', 'Testimonial', 'Training', 'Marketing', 'Internal comms', 'Sales', 'Recruitment', 'Brand story'],
  },
];

interface CustomTag { id: string; text: string; }
interface CustomCategory { id: string; name: string; color: string; tags: CustomTag[]; }

const CATEGORY_COLORS = ['#2E7BF6', '#00CEFF', '#00D744', '#F6A02E', '#C45EFF', '#FF6B6B', '#FFD93D', '#4ECDC4'];

function generateId() { return Math.random().toString(36).slice(2, 9); }

export default function ContentEnrichmentModal() {
  const dispatch = useAppDispatch();
  const overlayRef = useRef<HTMLDivElement>(null);

  // ── Search ──────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');

  // ── System categories: which are enabled ────────────────────────────────────
  const [enabledCategories, setEnabledCategories] = useState<Set<string>>(
    () => new Set(SYSTEM_CATEGORIES.map((c) => c.id))
  );

  // ── Custom categories ───────────────────────────────────────────────────────
  const [customCategories, setCustomCategories] = useState<CustomCategory[]>([]);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(CATEGORY_COLORS[0]);
  const [addingTag, setAddingTag] = useState<string | null>(null); // category id
  const [newTagText, setNewTagText] = useState('');

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dispatch(closeContentEnrichment()); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dispatch]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleCategory = (id: string) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const addCustomCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    setCustomCategories((prev) => [...prev, { id: generateId(), name, color: newCatColor, tags: [] }]);
    setNewCatName('');
    setNewCatColor(CATEGORY_COLORS[(customCategories.length + 1) % CATEGORY_COLORS.length]);
  };

  const deleteCustomCategory = (id: string) => {
    setCustomCategories((prev) => prev.filter((c) => c.id !== id));
  };

  const addTagToCategory = (catId: string) => {
    const text = newTagText.trim();
    if (!text) return;
    setCustomCategories((prev) =>
      prev.map((c) => c.id === catId ? { ...c, tags: [...c.tags, { id: generateId(), text }] } : c)
    );
    setNewTagText('');
    setAddingTag(null);
  };

  const deleteTag = (catId: string, tagId: string) => {
    setCustomCategories((prev) =>
      prev.map((c) => c.id === catId ? { ...c, tags: c.tags.filter((t) => t.id !== tagId) } : c)
    );
  };

  // ── Filtered system categories ───────────────────────────────────────────────
  const q = search.toLowerCase();
  const filteredSystem = SYSTEM_CATEGORIES.map((cat) => ({
    ...cat,
    tags: q ? cat.tags.filter((t) => t.toLowerCase().includes(q) || cat.label.toLowerCase().includes(q)) : cat.tags,
  })).filter((cat) => !q || cat.tags.length > 0 || cat.label.toLowerCase().includes(q));

  const filteredCustom = customCategories.map((cat) => ({
    ...cat,
    tags: q ? cat.tags.filter((t) => t.text.toLowerCase().includes(q) || cat.name.toLowerCase().includes(q)) : cat.tags,
  })).filter((cat) => !q || cat.tags.length > 0 || cat.name.toLowerCase().includes(q));

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) dispatch(closeContentEnrichment()); }}
      aria-modal="true"
      role="dialog"
      aria-label="Content Enrichment"
    >
      <div className={styles.modal}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <div>
              <h2 className={styles.title}>Content Enrichment</h2>
              <p className={styles.subtitle}>Configure tags and categories applied to every video in your library</p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={() => dispatch(closeContentEnrichment())} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search tags or categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {search && (
            <button className={styles.searchClear} onClick={() => setSearch('')} aria-label="Clear search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <div className={styles.body}>
          {/* ── System Default Categories ── */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleGroup}>
                <span className={styles.sectionBadge}>System</span>
                <h3 className={styles.sectionTitle}>Pre-selected Tag Categories</h3>
              </div>
              <p className={styles.sectionDesc}>These categories are automatically detected on every video. Toggle to enable or disable a category.</p>
            </div>

            <div className={styles.categoryGrid}>
              {filteredSystem.map((cat) => {
                const enabled = enabledCategories.has(cat.id);
                return (
                  <div key={cat.id} className={`${styles.categoryCard} ${enabled ? styles.categoryCardEnabled : styles.categoryCardDisabled}`}>
                    <div className={styles.categoryCardHeader}>
                      <div className={styles.categoryCardIcon}>{cat.icon}</div>
                      <span className={styles.categoryCardLabel}>{cat.label}</span>
                      <button
                        className={`${styles.toggle} ${enabled ? styles.toggleOn : ''}`}
                        onClick={() => toggleCategory(cat.id)}
                        aria-pressed={enabled}
                        aria-label={`${enabled ? 'Disable' : 'Enable'} ${cat.label}`}
                      >
                        <span className={styles.toggleThumb} />
                      </button>
                    </div>
                    <div className={styles.tagWrap}>
                      {cat.tags.map((tag) => (
                        <span key={tag} className={styles.systemTag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {filteredSystem.length === 0 && (
                <p className={styles.emptyHint}>No system categories match your search.</p>
              )}
            </div>
          </section>

          {/* ── Custom Categories ── */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleGroup}>
                <span className={`${styles.sectionBadge} ${styles.sectionBadgeCustom}`}>Custom</span>
                <h3 className={styles.sectionTitle}>Your Industry Categories</h3>
              </div>
              <p className={styles.sectionDesc}>Create categories and tags specific to your industry or workflow. These will be added to your AI tagging model.</p>
            </div>

            {/* Create new category row */}
            <div className={styles.newCatRow}>
              <div className={styles.colorPicker}>
                {CATEGORY_COLORS.map((c) => (
                  <button
                    key={c}
                    className={`${styles.colorSwatch} ${newCatColor === c ? styles.colorSwatchActive : ''}`}
                    style={{ background: c }}
                    onClick={() => setNewCatColor(c)}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
              <input
                className={styles.newCatInput}
                type="text"
                placeholder="Category name (e.g. Brand Elements, Products)"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCustomCategory()}
              />
              <button
                className={styles.newCatBtn}
                onClick={addCustomCategory}
                disabled={!newCatName.trim()}
              >
                + Add Category
              </button>
            </div>

            {/* Custom category cards */}
            {(q ? filteredCustom : customCategories).length === 0 && !q && (
              <div className={styles.emptyCustom}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
                <p>No custom categories yet. Add one above to start building your industry taxonomy.</p>
              </div>
            )}

            <div className={styles.customCategoryList}>
              {(q ? filteredCustom : customCategories).map((cat) => (
                <div key={cat.id} className={styles.customCategoryCard}>
                  <div className={styles.customCatHeader}>
                    <span className={styles.customCatDot} style={{ background: cat.color }} />
                    <span className={styles.customCatName}>{cat.name}</span>
                    <span className={styles.customCatCount}>{cat.tags.length} tag{cat.tags.length !== 1 ? 's' : ''}</span>
                    <button className={styles.deleteCatBtn} onClick={() => deleteCustomCategory(cat.id)} aria-label={`Delete ${cat.name}`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <div className={styles.customTagWrap}>
                    {cat.tags.map((tag) => (
                      <span key={tag.id} className={styles.customTag} style={{ borderColor: `${cat.color}40`, color: cat.color }}>
                        {tag.text}
                        <button
                          className={styles.customTagDelete}
                          onClick={() => deleteTag(cat.id, tag.id)}
                          aria-label={`Remove tag ${tag.text}`}
                        >×</button>
                      </span>
                    ))}
                    {addingTag === cat.id ? (
                      <div className={styles.addTagInline}>
                        <input
                          className={styles.addTagInput}
                          autoFocus
                          type="text"
                          placeholder="Tag name…"
                          value={newTagText}
                          onChange={(e) => setNewTagText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') addTagToCategory(cat.id);
                            if (e.key === 'Escape') { setAddingTag(null); setNewTagText(''); }
                          }}
                        />
                        <button className={styles.addTagConfirm} onClick={() => addTagToCategory(cat.id)}>Add</button>
                        <button className={styles.addTagCancel} onClick={() => { setAddingTag(null); setNewTagText(''); }}>Cancel</button>
                      </div>
                    ) : (
                      <button className={styles.addTagBtn} onClick={() => setAddingTag(cat.id)} style={{ borderColor: `${cat.color}40`, color: cat.color }}>
                        + Add tag
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {q && filteredCustom.length === 0 && customCategories.length > 0 && (
                <p className={styles.emptyHint}>No custom categories match your search.</p>
              )}
            </div>
          </section>
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <span className={styles.footerHint}>
            Changes are applied to new uploads. Re-index existing videos to apply retroactively.
          </span>
          <div className={styles.footerActions}>
            <button className={styles.cancelBtn} onClick={() => dispatch(closeContentEnrichment())}>Cancel</button>
            <button className={styles.saveBtn} onClick={() => dispatch(closeContentEnrichment())}>Save Settings</button>
          </div>
        </div>
      </div>
    </div>
  );
}
